import dynamic from 'next/dynamic';
import { ComponentProps, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import logPrintMapButtonClicked from '@alltrails/amplitude/events/logPrintMapButtonClicked';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import logTrailDetailsWebtoAppModuleClicked from '@alltrails/amplitude/events/logTrailDetailsWebtoAppModuleClicked';
import useUser from '@alltrails/context/hooks/useUser';
import { isSubscriber } from '@alltrails/context/utils/isSubscriber';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import DropdownMenu from '@alltrails/denali/components/DropdownMenu';
import IconButton from '@alltrails/denali/components/IconButton';
import PaidFeatureBadge from '@alltrails/denali/components/SubscriptionBadge/PaidFeatureBadge';
import Overflow from '@alltrails/denali/icons/Overflow';
import Print from '@alltrails/denali/icons/Print';
import Upload from '@alltrails/denali/icons/Upload';
import Watch from '@alltrails/denali/icons/Watch';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import usePrintModalExperiment from '@alltrails/experiments/hooks/usePrintModalExperiment';
import usePrintUrlSearchParams from '@alltrails/maps/hooks/usePrintUrlSearchParams';
import preloadPrint from '@alltrails/print/utils/preloadPrint';
import type { TrailPage } from '@alltrails/trails/types/trailPage';
import type TrailDetails from '@alltrails/types/TrailDetails';
import getTrailPageType from '@/utils/getTrailPageType';
import { useAppDispatch } from '@/utils/redux';
import { requestPrint } from '@/utils/trailPageSlice';
import styles from './DesktopNav.module.scss';

const PrintUpsellModal = dynamic(() => import('@alltrails/upsell/components/PrintUpsellModal/PrintUpsellModal'), { ssr: false });
const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });
const HitTheTrailModal = dynamic(() => import('@/components/HitTheTrailModal/HitTheTrailModal'), { ssr: false });
const GarminUpsellModal = dynamic(() => import('@alltrails/upsell/components/GarminUpsellModal/GarminUpsellModal'), { ssr: false });

type SignUpAction = 'export' | 'garmin' | 'print';

type OverflowMenuVariantProps = {
  trailDetails: TrailDetails;
  trailPage: TrailPage;
};

const OverflowMenuVariant = ({ trailDetails, trailPage }: OverflowMenuVariantProps) => {
  const intl = useIntl();
  const user = useUser();
  const experiments = useExperiments();
  const isDarkModeCtaExpOn = experiments['web-disco-rm-dark-mode-cta']?.value === 'treatment';
  const plusBadge = isDarkModeCtaExpOn ? (
    <PaidFeatureBadge badgeStyle="container" featureTier="plus" size="sm" />
  ) : (
    <PaidFeatureBadge featureTier="plus" size="sm" variant="icon" />
  );
  const { id } = trailDetails;
  const languageRegionCode = useLanguageRegionCode();
  const getPrintUrlSearchParams = usePrintUrlSearchParams();
  const { isEnabled: isPrintModalEnabled, sendExposure: sendPrintModalExposure } = usePrintModalExperiment();
  const dispatch = useAppDispatch();

  const [isOverflowDropdownOpen, setIsOverflowDropdownOpen] = useState(false);
  const [isHitTheTrailModalOpen, setIsHitTheTrailModalOpen] = useState(false);
  const [hitTheTrailModalSteps, setHitTheTrailModalSteps] = useState<ComponentProps<typeof HitTheTrailModal>['steps']>(['initial']);
  const [signUpModalState, setSignUpModalState] = useState<{ isOpen: false } | { isOpen: true; action: SignUpAction }>({ isOpen: false });
  const [isGarminUpsellModalOpen, setIsGarminUpsellModalOpen] = useState(false);
  const [isPrintMapUpsellModalOpen, setIsPrintMapUpsellModalOpen] = useState(false);
  const trailPageType = getTrailPageType(trailDetails);

  return (
    <>
      <DropdownMenu
        className={styles.overflowDropdown}
        isOpen={isOverflowDropdownOpen}
        onCloseRequest={() => setIsOverflowDropdownOpen(false)}
        anchor={
          <IconButton
            icon={{ Component: Overflow }}
            onClick={() => {
              const isOpening = !isOverflowDropdownOpen;
              // Print map is in here, and it opens the map straight into the modal — warming the
              // chunks now means the auto-open is not racing three cold imports and a map fetch
              if (isOpening && isPrintModalEnabled) {
                preloadPrint();
              }
              setIsOverflowDropdownOpen(isOpening);
            }}
            size="md"
            title={intl.formatMessage({ defaultMessage: 'More options' })}
            variant="secondary"
          />
        }
        placement="bottom-end"
        options={[
          {
            title: intl.formatMessage({ defaultMessage: 'Send to Garmin' }),
            onClick: () => {
              setIsOverflowDropdownOpen(false);
              logTrailDetailsActionTapped({
                trail_id: id.toString(),
                trail_page_action: 'send to garmin',
                trail_page_location: 'header bar',
                trail_page_type: trailPageType
              });
              logTrailDetailsWebtoAppModuleClicked({
                trail_id: id.toString(),
                was_plus_when_web_to_app_started: isSubscriber(user),
                web_to_app_action: 'send to garmin',
                trail_page_type: trailPageType
              });

              if (!user) {
                setSignUpModalState({ isOpen: true, action: 'garmin' });
                return;
              }

              if (!isSubscriber(user)) {
                setIsGarminUpsellModalOpen(true);
                return;
              }

              setHitTheTrailModalSteps(['garmin']);
              setIsHitTheTrailModalOpen(true);
            },
            leadingIcon: { icon: { Component: Watch } },
            trailingIcon: { customElement: plusBadge },
            className: styles.overflowDropdownItem
          },
          {
            title: intl.formatMessage({ defaultMessage: 'Print map' }),
            leadingIcon: { icon: { Component: Print } },
            onClick: () => {
              setIsOverflowDropdownOpen(false);
              logTrailDetailsActionTapped({
                trail_id: String(trailDetails.id),
                trail_page_action: 'print',
                trail_page_location: 'header bar',
                trail_page_type: trailPageType
              });
              logPrintMapButtonClicked({ map_location: 'trail', trail_id: trailDetails.id });
              sendPrintModalExposure();

              if (!user) {
                setSignUpModalState({ isOpen: true, action: 'print' });
                return;
              }

              if (isPrintModalEnabled) {
                dispatch(requestPrint());
                return;
              }

              if (!user?.featureEntitlements?.includes('print_maps')) {
                setIsPrintMapUpsellModalOpen(true);
                return;
              }

              window.location.assign(wrapUrlSafe(`/trail/${trailDetails.slug}/print?${getPrintUrlSearchParams().toString()}`, languageRegionCode));
            },
            trailingIcon: { customElement: plusBadge },
            className: styles.overflowDropdownItem
          },
          {
            title: intl.formatMessage({ defaultMessage: 'Export map file' }),
            onClick: () => {
              setIsOverflowDropdownOpen(false);
              logTrailDetailsActionTapped({
                trail_id: id.toString(),
                trail_page_action: 'export map file',
                trail_page_location: 'header bar',
                trail_page_type: trailPageType
              });
              logTrailDetailsWebtoAppModuleClicked({
                trail_id: id.toString(),
                was_plus_when_web_to_app_started: isSubscriber(user),
                web_to_app_action: 'export map file',
                trail_page_type: trailPageType
              });

              if (!user) {
                setSignUpModalState({ isOpen: true, action: 'export' });
                return;
              }

              setHitTheTrailModalSteps(['export']);
              setIsHitTheTrailModalOpen(true);
            },
            leadingIcon: { icon: { Component: Upload } },
            className: styles.overflowDropdownItem
          }
        ]}
      />

      {isHitTheTrailModalOpen && (
        <HitTheTrailModal
          includeOpenInApp={false}
          includeSendToGarmin={hitTheTrailModalSteps?.includes('garmin') ?? false}
          includeExportMapFile={hitTheTrailModalSteps?.includes('export') ?? false}
          isOpen={isHitTheTrailModalOpen}
          onCloseRequest={() => {
            setIsHitTheTrailModalOpen(false);
          }}
          steps={hitTheTrailModalSteps}
          title={
            hitTheTrailModalSteps && hitTheTrailModalSteps[0] === 'export' ? (
              <FormattedMessage defaultMessage="Export map file" />
            ) : (
              <FormattedMessage defaultMessage="Send to Garmin" />
            )
          }
          trailDetails={trailDetails}
          trailPage={trailPage}
        />
      )}

      {signUpModalState.isOpen && (
        <SignUpModal
          trigger={(() => {
            switch (signUpModalState.action) {
              case 'garmin':
                return 'Send Routes To Garmin';
              case 'print':
                return 'Print Map';
              default:
                return 'Export Map File';
            }
          })()}
          closeModal={() => setSignUpModalState({ isOpen: false })}
          isOpen={signUpModalState.isOpen}
          onSuccess={loggedInUser => {
            setSignUpModalState({ isOpen: false });

            if (signUpModalState.action === 'print' && isPrintModalEnabled) {
              dispatch(requestPrint());
            } else if (signUpModalState.action === 'garmin' && !isSubscriber(loggedInUser)) {
              setIsGarminUpsellModalOpen(true);
            } else if (signUpModalState.action === 'print' && !loggedInUser?.featureEntitlements?.includes('print_maps')) {
              setIsPrintMapUpsellModalOpen(true);
            } else if (signUpModalState.action !== 'print') {
              setHitTheTrailModalSteps([signUpModalState.action]);
              setIsHitTheTrailModalOpen(true);
            }
          }}
          upsellTrigger={(() => {
            switch (signUpModalState.action) {
              case 'garmin':
                return 'Send Routes To Garmin';
              case 'print':
                return 'Print Map';
              default:
                return 'Export Map File';
            }
          })()}
          deeplinkData={{ pageId: id, pageType: 'trail' }}
        />
      )}

      {isGarminUpsellModalOpen && (
        <GarminUpsellModal
          isOpen={isGarminUpsellModalOpen}
          onCloseRequest={() => {
            setIsGarminUpsellModalOpen(false);
          }}
        />
      )}

      {isPrintMapUpsellModalOpen && (
        <PrintUpsellModal isOpen={isPrintMapUpsellModalOpen} onCloseRequest={() => setIsPrintMapUpsellModalOpen(false)} upsellTrigger="Print Map" />
      )}
    </>
  );
};

export default OverflowMenuVariant;
